import { useEffect } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import PropTypes from 'prop-types';
import Analytics from '@utils/Analytics';
import store from 'store';
import {
    storeTokenInBackend,
    updatePushInhibitedSetting,
    LOCALSTORAGE_KEY_DEVICE_TOKEN,
} from './PushNotificationPrompt.react';
import { ensureRegistrationListeners } from '@utils/pushNotificationService';

const extractTitleAndBody = (notification, platform) => {
    if (platform === 'android') {
        // On Android, title and body are often in the data object
        return {
            title: notification.data?.title || notification.title,
            body: notification.data?.body || notification.body,
        };
    }
    
    // For iOS and other platforms, use the standard structure
    return {
        title: notification.title,
        body: notification.body,
    };
};

const PushNotificationsHandler = (props, context) => {
    useEffect(() => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            return;
        }

        const platform = Capacitor.getPlatform();
        let receivedListener, actionListener;

        (async () => {
            await ensureRegistrationListeners(
                (token) => {
                    if (token.value && token.value !== store.get(LOCALSTORAGE_KEY_DEVICE_TOKEN)) {
                        store.set(LOCALSTORAGE_KEY_DEVICE_TOKEN, token.value);
                        storeTokenInBackend(token.value, platform);
                    }
                },
                (error) => console.warn('Silent push registration error:', error)
            );

            if (platform === 'ios' || platform === 'android') {
                const storedToken = store.get(LOCALSTORAGE_KEY_DEVICE_TOKEN);
                if (storedToken) {
                    const permissions = await PushNotifications.checkPermissions();

                    if (permissions.receive !== 'granted') {
                        // Sync backend when permission is denied
                        await updatePushInhibitedSetting(true);
                        return;
                    }

                    try {
                        await PushNotifications.register();
                    } catch (err) {
                        console.warn('Silent push token refresh failed:', err);
                    }
                }

                receivedListener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
                    const { id, data } = notification;
                    const { title, body } = extractTitleAndBody(notification, platform);
                    const { image, url, backend_notification_id } = data || {};
                    Analytics.trackPushNotificationDelivered({
                        'Notification ID': id,
                        'Backend Notification ID': backend_notification_id,
                        Title: title,
                        Body: body,
                        Image: image,
                        URL: url,
                        'Device Type': platform,
                    });
                });

                actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                    PushNotifications.removeAllDeliveredNotifications();
                    const { id, data } = notification.notification;
                    const { title, body } = extractTitleAndBody(notification.notification, platform);
                    const { image, url, backend_notification_id } = data || {};

                    Analytics.trackPushNotificationOpened({
                        'Notification ID': id,
                        'Backend Notification ID': backend_notification_id,
                        Title: title,
                        Body: body,
                        Image: image,
                        URL: url,
                        'Device Type': platform,
                    });

                    if (url) {
                        Analytics.trackPushNotificationNavigated({
                            'Notification ID': id,
                            'Backend Notification ID': backend_notification_id,
                            Title: title,
                            Body: body,
                            Image: image,
                            URL: url,
                            'Device Type': platform,
                        });
                        if (context.router) {
                            // Workaround: If the app is already on /meals, React Router won't detect a navigation change
                            // because it's technically the "same page" with different query params.
                            // To force React Router to recognize the navigation, we first navigate to /my-account
                            // (a different page) and then immediately navigate to the intended URL after a short delay.
                            if (url.startsWith('/meals')) {
                                context.router.push('/my-account');
                            }
                            context.router.push(url);
                        }
                    }
                });
            }
        })();

        return () => {
            receivedListener?.remove();
            actionListener?.remove();
        };
    }, []);

    return null;
};

PushNotificationsHandler.contextTypes = {
    router: PropTypes.object,
};

export default PushNotificationsHandler;
